import { useState, useCallback, useEffect, useRef } from 'react';
import {
  ToolGroupManager,
  addTool,
  annotation as csAnnotation,
  Enums as ToolEnums,
  PanTool,
  ZoomTool,
  WindowLevelTool,
  LengthTool,
  AngleTool,
  PlanarFreehandROITool,
  drawing as csDrawing,
  utilities as csToolUtils,
} from '@cornerstonejs/tools';
import {
  VelocityProbeTool,
  VelocitySegmentTool,
  VTITraceTool,
  TimeIntervalTool,
  formatDopplerTextLines,
  type SpectralDopplerValues,
  type SpectralDopplerToolVariant,
} from '@/features/study-detail/Viewer/tools/SpectralDoppler';
import { MouseBindings } from '@cornerstonejs/tools/enums';
import {
  utilities as csUtils,
  eventTarget,
  getEnabledElement,
} from '@cornerstonejs/core';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  selectAnnotation,
  selectSelectedAnnotationUID,
  type SelectedImageTools,
} from 'state/slices/measurementSession';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import { applySmartLabelAnchor } from '@/features/study-detail/Viewer/tools/labelPlacement';
import { deleteAnnotationWithHistory } from '@/features/study-detail/history/annotationLifecycle';
import type { RootState } from '@/state/store';
import { annotationUIDAtEvent } from '@/features/study-detail/Viewer/utils/annotationAtPoint';
import { getBaseAnnotationStyle } from '@/features/study-detail/Viewer/utils/annotationStyle';
import {
  crosshairMetrics,
  drawEndpointCrosshairs,
} from '@/features/study-detail/Viewer/utils/endpointCrosshair';
import {
  SIMPSON_TOOL_NAME,
  canUseViewerTool,
} from '@/utils/measurementToolMapping';
import { keepHoverOnSelected, orderForHitTest } from '../tools/hitTestOrder';
import {
  renderContourEditPreview,
  renderEndEditPreview,
  type ContourEditPreviewTool,
  type RenderContour,
} from '../tools/contourEditPreview';
import {
  endEditDrawData,
  stageEdit,
  type StagedDrawData,
  type StagedEditData,
} from '../tools/stagedContourEdit';

/** The Cornerstone tool behind a viewer tool: Simpson traces with the freehand. */
const cornerstoneTool = (tool: string) =>
  tool === SIMPSON_TOOL_NAME ? PlanarFreehandROITool.toolName : tool;

// ---------------------------------------------------------------------------
// Tool categorisation
// ---------------------------------------------------------------------------
// Manipulation tools (W/L, Pan, Zoom) modify viewport properties and produce
// no persistent annotations. They are activated on ALL viewports at once so
// the user can interact with any viewport without re-activation on hover.
// This is critical for VideoViewport W/L: calling setToolPassive triggers an
// internal re-render that invalidates the CSS SVG filter used for brightness/
// contrast. Keeping the tool active avoids that re-render entirely.
//
// Annotation tools (Length, Angle, Freehand) create persistent annotations and
// need single-viewport focus — they follow the deactivate-all/activate-one
// pattern on hover.
// ---------------------------------------------------------------------------

export const MANIPULATION_TOOLS = new Set([
  WindowLevelTool.toolName,
  PanTool.toolName,
  ZoomTool.toolName,
]);

/** Tools whose annotations a click can select. */
const SELECTABLE_TOOLS = [
  LengthTool.toolName,
  AngleTool.toolName,
  PlanarFreehandROITool.toolName,
  VelocityProbeTool.toolName,
  VelocitySegmentTool.toolName,
  VTITraceTool.toolName,
  TimeIntervalTool.toolName,
];

/** Doppler labels sit at fixed spots and show on hover even without a row. */
const DOPPLER_TOOLS = new Set<string>([
  VelocityProbeTool.toolName,
  VelocitySegmentTool.toolName,
  VTITraceTool.toolName,
]);
const isDoppler = (ann: unknown) =>
  DOPPLER_TOOLS.has(
    (ann as { metadata?: { toolName?: string } } | undefined)?.metadata
      ?.toolName ?? ''
  );

// Module-level mirror of the currently-selected annotation UID so the
// module-scope getTextLines callback can consult it. Kept in sync from the
// mousedown handler below. Step 5 replaces this with Redux state.
let currentSelectedUID: string | null = null;
// Mirror of the hovered annotation UID: locked annotations (finalized report)
// never get Cornerstone's hover flag, so the hover handler's hit test sets it.
let currentHoveredUID: string | null = null;

const isHovered = (
  ann: { annotationUID?: string; highlighted?: boolean } | null
) =>
  !!ann &&
  (!!ann.highlighted ||
    (!!ann.annotationUID && ann.annotationUID === currentHoveredUID));

// Look up an annotation by reference-equality on its `data` blob so the
// getTextLines callback (which only receives `data`) can figure out which
// annotation Cornerstone is rendering. Annotation count is small (a handful
// per study), so O(n) is fine.
function findAnnotationByData(data: unknown): {
  annotationUID?: string;
  highlighted?: boolean;
} | null {
  const all = csAnnotation.state.getAllAnnotations() as {
    annotationUID?: string;
    highlighted?: boolean;
    data?: unknown;
  }[];
  return all.find((a) => a.data === data) ?? null;
}

// Custom text formatters (same as useViewerTools.ts)
function customLengthGetTextLines(
  data: {
    cachedStats: Record<string, { length?: number; unit?: string }>;
    measurementLabel?: string;
    abbreviation?: string;
  },
  targetId: string
): string[] {
  const cachedStats = data.cachedStats?.[targetId];
  if (!cachedStats) return [];

  const { length } = cachedStats;
  if (length === undefined || length === null || Number.isNaN(length)) {
    return [];
  }

  // Drawing preview: measurementLabel is set at ANNOTATION_COMPLETED, so its
  // absence means the user is still dragging out the line (or drew without a
  // worksheet measurement selected). Fall back to showing the raw length so
  // interactive draw stays usable.
  if (!data.measurementLabel) {
    const lengthCm = length / 10;
    return [`${csUtils.roundNumber(lengthCm, 2)} cm`];
  }

  // Hovered → abbreviation chip, selected or not (the selected value also
  // shows in the pane header, SelectedAnnotationLabel). Inactive → nothing.
  const ann = findAnnotationByData(data);
  if (isHovered(ann) && data.abbreviation) return [data.abbreviation];
  return [];
}

interface FreehandAnnotation {
  data: { contour: { closed: boolean; polyline: CornerstoneTypes.Point3[] } };
}
interface FreehandSelectEvent {
  detail: {
    element: HTMLDivElement;
    currentPoints: { canvas: CornerstoneTypes.Point2 };
  };
  preventDefault: () => void;
}
type FreehandFuse = (evt: unknown) => CornerstoneTypes.Point2[] | undefined;
interface FreehandEndEditEvent {
  detail: { element: HTMLDivElement };
}
interface FreehandHitTest extends ContourEditPreviewTool {
  editData?: StagedEditData | null;
  drawData?: StagedDrawData | null;
  commonData?: {
    annotation: FreehandAnnotation & {
      data: { handles: { activeHandleIndex: number | null } };
    };
    viewportIdsToRender: string[];
  } | null;
  isEditingOpen: boolean;
  renderClosedContourBeingEdited: RenderContour;
  renderOpenContourBeingEdited: RenderContour;
  renderContourBeingDrawn: RenderContour;
  finishEditAndStartNewEdit: (evt: unknown) => void;
  finishEditOpenOnSecondCrossing: (evt: unknown) => void;
  fuseEditPointsWithClosedContour: FreehandFuse;
  fuseEditPointsWithOpenContour: FreehandFuse;
  fuseEditPointsForOpenContourEndEdit: () => CornerstoneTypes.Point2[];
  openContourEditOverwriteEnd: (evt: FreehandEndEditEvent) => void;
  activateOpenContourEndEdit: (
    evt: FreehandEndEditEvent,
    annotation: FreehandAnnotation,
    viewportIdsToRender: string[],
    handle: unknown
  ) => void;
  deactivateOpenContourEdit: (element: HTMLDivElement) => void;
  findCrossingIndexDuringCreate: (evt: unknown) => number | undefined;
  doneEditMemo: () => void;
  isPointNearTool: (
    element: HTMLDivElement,
    ann: FreehandAnnotation,
    canvasCoords: CornerstoneTypes.Point2,
    proximity: number
  ) => boolean;
  toolSelectedCallback: (
    evt: FreehandSelectEvent,
    ann: FreehandAnnotation,
    ...rest: unknown[]
  ) => void;
  renderLinkedTextBoxAnnotation: (options: FreehandTextBoxOptions) => boolean;
}
interface FreehandTextBox {
  worldPosition?: number[];
  hasMoved?: boolean;
  userMoved?: boolean;
  autoWorldPosition?: number[];
}
interface FreehandTextBoxOptions {
  annotation: { data: { handles: { textBox?: FreehandTextBox } } };
  canvasCoordinates: CornerstoneTypes.Point2[];
  enabledElement: { viewport: CornerstoneTypes.IViewport };
}

interface SimpsonStatsOnAnnotation {
  volume: number;
  volumeUnit: string;
  longAxisLength?: number;
  longAxisUnit?: string;
}

function customFreehandROIGetTextLines(
  data: {
    cachedStats: Record<string, { area?: number }>;
    handles?: { points?: { x: number; y: number }[] };
    simpsonStats?: SimpsonStatsOnAnnotation;
    measurementLabel?: string;
    abbreviation?: string;
  },
  targetId: string
): string[] {
  // Full readout (Area + Vol + L) — used only for the live drawing preview.
  // Perimeter is intentionally omitted from the volumetric readout (AP-542).
  const buildFullLines = (): string[] => {
    const cachedStats = data.cachedStats?.[targetId];
    const lines: string[] = [];
    // area is mm² (calibrated by pixel spacing) → cm².
    if (cachedStats?.area !== undefined && !Number.isNaN(cachedStats.area)) {
      lines.push(`Area: ${csUtils.roundNumber(cachedStats.area / 100, 2)} cm²`);
    }
    if (data.simpsonStats) {
      const { volume, volumeUnit, longAxisLength, longAxisUnit } =
        data.simpsonStats;
      lines.push(`Vol: ${csUtils.roundNumber(volume, 2)} ${volumeUnit}`);
      if (longAxisLength !== undefined && longAxisUnit !== undefined) {
        lines.push(
          `L: ${csUtils.roundNumber(longAxisLength, 2)} ${longAxisUnit}`
        );
      }
    }
    return lines;
  };

  // Drawing preview: measurementLabel is set at ANNOTATION_COMPLETED, so its
  // absence means the user is still tracing — show the live readout.
  if (!data.measurementLabel) return buildFullLines();

  // Hovered → abbreviation chip, selected or not; inactive → nothing.
  const ann = findAnnotationByData(data);
  if (isHovered(ann) && data.abbreviation) return [data.abbreviation];
  return [];
}

// Spectral-Doppler probe (POINT) on-image text. Three-state, identical in spirit
// to customLengthGetTextLines: the crosshair value lives in the pane header when
// selected, an abbreviation chip appears on hover, and nothing shows when idle.
// The velocity itself only renders during the live-draw preview (before the row
// label is attached) so a fresh click stays legible.
function customProbeGetTextLines(data: {
  abbreviation?: string;
  measurementLabel?: string;
  spectralValues?: SpectralDopplerValues;
  spectralToolVariant?: SpectralDopplerToolVariant;
}): string[] {
  // Drawing preview: measurementLabel is attached at ANNOTATION_COMPLETED, so
  // its absence means the probe is still being placed — show the live velocity.
  if (!data.measurementLabel) {
    return data.spectralValues && data.spectralToolVariant
      ? formatDopplerTextLines(data.spectralToolVariant, data.spectralValues)
      : [];
  }

  const ann = findAnnotationByData(data);
  if (isHovered(ann) && data.abbreviation) return [data.abbreviation];
  return [];
}

let toolsRegistered = false;

function registerTools() {
  if (toolsRegistered) return;

  addTool(PanTool);
  addTool(ZoomTool);
  addTool(WindowLevelTool);
  addTool(LengthTool);
  addTool(AngleTool);
  addTool(PlanarFreehandROITool);
  addTool(VelocityProbeTool);
  addTool(VelocitySegmentTool);
  addTool(VTITraceTool);
  addTool(TimeIntervalTool);

  toolsRegistered = true;
}

const TOOL_GROUP_PREFIX = 'shared-tool-group-';

interface UseSharedToolStateProps {
  activeViewportId: string | null;
  activeImage?: SelectedImageTools | null;
  renderingEngineId: string;
  renderingEngine: CornerstoneTypes.IRenderingEngine | null;
  viewportIds: string[];
  activeMeasurementLabel?: string | null;
  activeMeasurementAbbreviation?: string | null;
  canEdit?: boolean;
}

interface UseSharedToolStateReturn {
  activeTool: string | undefined;
  changeTool: (tool: string) => void;
  deactivateTool: () => void;
  setupToolGroupForViewport: (viewportId: string) => void;
  cleanupToolGroupForViewport: (viewportId: string) => void;
}

export function useSharedToolState({
  activeViewportId,
  activeImage,
  renderingEngineId,
  renderingEngine,
  viewportIds,
  activeMeasurementLabel,
  activeMeasurementAbbreviation,
  canEdit = true,
}: UseSharedToolStateProps): UseSharedToolStateReturn {
  const dispatch = useDispatch();
  const reduxStore = useStore<RootState>();
  const selectedAnnotationUID = useSelector(selectSelectedAnnotationUID);
  const [activeTool, setActiveTool] = useState<string | undefined>(undefined);
  const activeToolRef = useRef<string | undefined>(undefined);
  // Tracks whether the user explicitly activated the tool (hotkey/toolbar/measurement click)
  // vs Cornerstone internally activating for annotation drag.
  const userActivatedRef = useRef(false);
  const measurementLabelRef = useRef<string | null>(
    activeMeasurementLabel ?? null
  );
  measurementLabelRef.current = activeMeasurementLabel ?? null;
  const measurementAbbreviationRef = useRef<string | null>(
    activeMeasurementAbbreviation ?? null
  );
  measurementAbbreviationRef.current = activeMeasurementAbbreviation ?? null;
  // Kept in sync each render so the once-registered ANNOTATION_COMPLETED
  // listener can read the freshest viewport context for label auto-placement.
  const activeViewportIdRef = useRef<string | null>(activeViewportId);
  activeViewportIdRef.current = activeViewportId;
  const renderingEngineRef = useRef<CornerstoneTypes.IRenderingEngine | null>(
    renderingEngine
  );
  renderingEngineRef.current = renderingEngine;

  // Register tools once globally
  useEffect(() => {
    registerTools();
  }, []);

  // Setup tool group for a viewport
  const setupToolGroupForViewport = useCallback(
    (viewportId: string) => {
      if (!renderingEngine) return;

      const toolGroupId = `${TOOL_GROUP_PREFIX}${viewportId}`;

      // Check if tool group already exists
      let toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
      if (toolGroup) return;

      // Create new tool group
      toolGroup = ToolGroupManager.createToolGroup(toolGroupId);
      if (!toolGroup) return;

      // Add all tools to the group
      toolGroup.addTool(PanTool.toolName);
      toolGroup.addTool(ZoomTool.toolName);
      toolGroup.addTool(WindowLevelTool.toolName);
      toolGroup.addTool(LengthTool.toolName);
      toolGroup.addTool(AngleTool.toolName);
      toolGroup.addTool(PlanarFreehandROITool.toolName);
      toolGroup.addTool(VelocityProbeTool.toolName);
      toolGroup.addTool(VelocitySegmentTool.toolName);
      toolGroup.addTool(VTITraceTool.toolName);
      toolGroup.addTool(TimeIntervalTool.toolName);

      // Configure custom text formatters
      toolGroup.setToolConfiguration(LengthTool.toolName, {
        getTextLines: customLengthGetTextLines,
      });
      toolGroup.setToolConfiguration(PlanarFreehandROITool.toolName, {
        getTextLines: customFreehandROIGetTextLines,
      });
      // Doppler probe follows the same three-state chip as Length (replaces the
      // tool's always-on defaultGetTextLines).
      toolGroup.setToolConfiguration(VelocityProbeTool.toolName, {
        getTextLines: customProbeGetTextLines,
      });

      // AP-835: a click anywhere inside a volume selects it; open traces count as closed end-to-start.
      const freehand = toolGroup.getToolInstance(
        PlanarFreehandROITool.toolName
      ) as unknown as FreehandHitTest | undefined;
      if (freehand) {
        const isNearContour = freehand.isPointNearTool;
        const editContour = freehand.toolSelectedCallback;
        freehand.isPointNearTool = (element, ann, canvasCoords, proximity) => {
          if (isNearContour(element, ann, canvasCoords, proximity)) return true;
          const { contour } = ann.data;
          const viewport = getEnabledElement(element)?.viewport;
          if (!viewport) return false;
          return csToolUtils.math.polyline.containsPoint(
            contour.polyline.map((p) => viewport.worldToCanvas(p)),
            canvasCoords
          );
        };
        // Interior clicks only select; reshaping stays on the edge.
        freehand.toolSelectedCallback = (evt, ann, ...rest) => {
          const { element, currentPoints } = evt.detail;
          if (isNearContour(element, ann, currentPoints.canvas, 6)) {
            editContour(evt, ann, ...rest);
            return;
          }
          evt.preventDefault();
        };
        // The value label sits below the contour and follows it while editing.
        const renderTextBox =
          freehand.renderLinkedTextBoxAnnotation.bind(freehand);
        freehand.renderLinkedTextBoxAnnotation = (options) => {
          const { annotation, canvasCoordinates, enabledElement } = options;
          const { textBox } = annotation.data.handles;
          // Moved off our last placement: the reader dragged it, so it stays.
          if (
            textBox?.autoWorldPosition &&
            textBox.worldPosition &&
            textBox.worldPosition.some(
              (v, i) => v !== textBox.autoWorldPosition?.[i]
            )
          ) {
            textBox.userMoved = true;
          }
          applySmartLabelAnchor({
            annotation,
            geometryCanvasPoints: canvasCoordinates,
            viewport: enabledElement.viewport as unknown as {
              element: HTMLElement;
              canvasToWorld: (
                p: CornerstoneTypes.Point2
              ) => CornerstoneTypes.Point3;
            },
            // Volumes and areas: beside the contour's right-most point.
            spot: {
              point: canvasCoordinates.reduce((a, b) => (b[0] > a[0] ? b : a)),
              side: 'right',
            },
          });
          const placed = annotation.data.handles.textBox;
          if (placed && !placed.userMoved && placed.worldPosition) {
            placed.autoWorldPosition = [...placed.worldPosition];
          }
          return renderTextBox(options);
        };
        // AP-872: a reshape keeps the contour, draws its drag path dotted and applies on release.
        freehand.renderClosedContourBeingEdited = (...args) =>
          renderContourEditPreview(freehand, ...args);
        freehand.renderOpenContourBeingEdited = (...args) =>
          renderContourEditPreview(freehand, ...args);
        const renderDrawn = freehand.renderContourBeingDrawn;
        freehand.renderContourBeingDrawn = (...args) =>
          freehand.drawData?.extensionFrom === undefined
            ? renderDrawn(...args)
            : renderEndEditPreview(freehand, ...args);
        // Each crossing back over the contour is staged, not saved; release writes the result.
        const stage = () => {
          if (freehand.editData)
            freehand.editData = stageEdit(freehand.editData);
        };
        freehand.finishEditAndStartNewEdit = stage;
        freehand.finishEditOpenOnSecondCrossing = stage;
        const fuseClosed = freehand.fuseEditPointsWithClosedContour;
        freehand.fuseEditPointsWithClosedContour = (evt) =>
          fuseClosed(evt) ?? freehand.editData?.stagedCanvasPoints;
        const fuseOpen = freehand.fuseEditPointsWithOpenContour;
        freehand.fuseEditPointsWithOpenContour = (evt) =>
          fuseOpen(evt) ?? freehand.editData?.stagedCanvasPoints;
        // Extending an open trace from its end: unchanged until release, never closed mid-drag.
        const activateEndEdit = freehand.activateOpenContourEndEdit;
        freehand.activateOpenContourEndEdit = (...args) => {
          activateEndEdit(...args);
          if (freehand.drawData) {
            freehand.drawData.extensionFrom =
              freehand.drawData.canvasPoints.length - 1;
          }
        };
        freehand.openContourEditOverwriteEnd = (evt) => {
          const { commonData, editData } = freehand;
          if (!commonData || !editData) return;
          const fused = freehand.fuseEditPointsForOpenContourEndEdit();
          commonData.annotation.data.handles.activeHandleIndex = 1;
          freehand.isEditingOpen = false;
          freehand.editData = undefined;
          freehand.commonData = undefined;
          freehand.doneEditMemo();
          freehand.deactivateOpenContourEdit(evt.detail.element);
          freehand.activateOpenContourEndEdit(
            evt,
            commonData.annotation,
            commonData.viewportIdsToRender,
            null
          );
          freehand.drawData = endEditDrawData(fused, editData);
        };
        const findCrossing = freehand.findCrossingIndexDuringCreate;
        freehand.findCrossingIndexDuringCreate = (evt) =>
          freehand.drawData?.extensionFrom === undefined
            ? findCrossing(evt)
            : undefined;
      }

      // Patch the LengthTool instance so the text label AND its leader line
      // both anchor to the midpoint of the line instead of the right-side
      // endpoint. Cornerstone uses `placementPoints` for text positioning
      // and `canvasCoordinates` as the candidate set for the leader line's
      // closest-point search — we override both to a single midpoint.
      const lengthToolInstance = toolGroup.getToolInstance(LengthTool.toolName);
      if (lengthToolInstance) {
        const originalRender =
          lengthToolInstance.renderLinkedTextBoxAnnotation.bind(
            lengthToolInstance
          );
        lengthToolInstance.renderLinkedTextBoxAnnotation = (
          options: Record<string, unknown>
        ) => {
          // Honor a moved textBox — the hover chip pins it to the cursor — so
          // the label sits at the pointer instead of the line midpoint.
          const textBox = (
            options.annotation as
              | {
                  data?: {
                    handles?: {
                      textBox?: {
                        hasMoved?: boolean;
                        worldPosition?: number[];
                      };
                    };
                  };
                }
              | undefined
          )?.data?.handles?.textBox;
          const vp = (
            options.enabledElement as
              | { viewport?: { worldToCanvas?: (w: number[]) => number[] } }
              | undefined
          )?.viewport;
          if (textBox?.hasMoved && textBox.worldPosition && vp?.worldToCanvas) {
            const anchor = vp.worldToCanvas(textBox.worldPosition);
            return originalRender({
              ...options,
              placementPoints: [anchor],
              canvasCoordinates: [anchor],
            });
          }
          const coords = options.canvasCoordinates as number[][];
          if (coords?.length >= 2) {
            const midpoint = [
              (coords[0][0] + coords[1][0]) / 2,
              (coords[0][1] + coords[1][1]) / 2,
            ];
            return originalRender({
              ...options,
              placementPoints: [midpoint],
              canvasCoordinates: [midpoint],
            });
          }
          return originalRender(options);
        };

        // `showHandlesAlways` is global-only, so endpoint styling can't be
        // scoped per-annotation — augment the tool's render instead (AP-810).
        const instance = lengthToolInstance as unknown as {
          renderAnnotation: (
            enabledElement: { viewport: CornerstoneTypes.IViewport },
            svgDrawingHelper: Parameters<typeof csDrawing.drawLine>[0]
          ) => boolean;
          filterInteractableAnnotationsForElement: (
            element: HTMLElement,
            annotations: unknown[]
          ) => unknown[] | undefined;
          getAnnotationStyle: (ctx: {
            annotation: unknown;
            styleSpecifier: unknown;
          }) => {
            color?: string;
            lineWidth?: number;
            lineDash?: string;
          };
          toolGroupId: string;
        };
        const originalRenderAnnotation =
          instance.renderAnnotation.bind(lengthToolInstance);
        instance.renderAnnotation = (enabledElement, svgDrawingHelper) => {
          const status = originalRenderAnnotation(
            enabledElement,
            svgDrawingHelper
          );
          const viewport =
            enabledElement.viewport as CornerstoneTypes.IViewport & {
              element: HTMLElement;
              id: string;
              worldToCanvas: (p: number[]) => number[];
            };

          // Endpoints stay visible on every linear measurement, not just the
          // selected one (AP-772); prominence comes from the per-annotation style.
          const annotations = csAnnotation.state.getAnnotations(
            LengthTool.toolName,
            viewport.element
          ) as
            | {
                annotationUID?: string;
                metadata?: { toolName?: string };
                data?: { handles?: { points?: number[][] } };
              }[]
            | undefined;
          if (!annotations?.length) return status;

          const interactable =
            (instance.filterInteractableAnnotationsForElement(
              viewport.element,
              annotations
            ) ?? []) as {
              annotationUID?: string;
              data?: { handles?: { points?: number[][] } };
            }[];

          interactable.forEach((annotation) => {
            const uid = annotation.annotationUID;
            const points = annotation?.data?.handles?.points;
            if (!uid || !points || points.length < 2) return;

            const styleSpecifier = {
              toolGroupId: instance.toolGroupId,
              toolName: LengthTool.toolName,
              viewportId: viewport.id,
              annotationUID: uid,
            };
            const { color } = instance.getAnnotationStyle({
              annotation,
              styleSpecifier,
            }) as { color?: string; lineWidth?: number; lineDash?: string };
            const canvasPoints = points.map(
              (p: number[]) =>
                viewport.worldToCanvas(p) as CornerstoneTypes.Point2
            );
            drawEndpointCrosshairs(svgDrawingHelper, uid, canvasPoints, {
              color,
              ...crosshairMetrics(uid === currentSelectedUID),
            });
          });
          return status;
        };
      }

      // Add viewport to the tool group
      toolGroup.addViewport(viewportId, renderingEngineId);

      // Hide leader lines at the tool-group level. `textBoxLinkLineWidth: 0`
      // is a trap — Cornerstone falls back to width=10 when the value is
      // falsy — so we use `textBoxLinkLineColor: 'transparent'` instead.
      csAnnotation.config.style.setToolGroupToolStyles(toolGroupId, {
        [LengthTool.toolName]: {
          textBoxLinkLineColor: 'transparent',
          textBoxLinkLineDash: '',
        },
        [PlanarFreehandROITool.toolName]: {
          textBoxLinkLineColor: 'transparent',
          textBoxLinkLineDash: '',
        },
        // Doppler tools: kill the dashed leader line so the hover/selected chip
        // reads as a clean chip like Length/Freehand.
        [VelocityProbeTool.toolName]: {
          textBoxLinkLineColor: 'transparent',
          textBoxLinkLineDash: '',
        },
        [VelocitySegmentTool.toolName]: {
          textBoxLinkLineColor: 'transparent',
          textBoxLinkLineDash: '',
        },
        [VTITraceTool.toolName]: {
          textBoxLinkLineColor: 'transparent',
          textBoxLinkLineDash: '',
        },
        [TimeIntervalTool.toolName]: {
          textBoxLinkLineColor: 'transparent',
          textBoxLinkLineDash: '',
        },
      });

      // The selected annotation owns its click area (AP-806): hit-testing
      // sees it first. Rendering is left exactly as Cornerstone orders it;
      // hover arbitration happens once, across tools, in the mousemove handler.
      [LengthTool, AngleTool, PlanarFreehandROITool].forEach((Tool) => {
        interface Ann {
          annotationUID?: string;
          highlighted?: boolean;
        }
        const tool = toolGroup.getToolInstance(Tool.toolName) as {
          filterInteractableAnnotationsForElement: (
            element: HTMLDivElement,
            annotations: Ann[]
          ) => Ann[] | undefined;
          renderAnnotation: (...args: unknown[]) => boolean;
        };
        const selectedUID = () =>
          selectSelectedAnnotationUID(reduxStore.getState());
        const originalFilter =
          tool.filterInteractableAnnotationsForElement.bind(tool);
        const originalRender = tool.renderAnnotation.bind(tool);
        let rendering = false;
        tool.filterInteractableAnnotationsForElement = (
          element,
          annotations
        ) => {
          const visible = originalFilter(element, annotations) ?? [];
          return rendering ? visible : orderForHitTest(visible, selectedUID());
        };
        tool.renderAnnotation = (...args) => {
          rendering = true;
          try {
            return originalRender(...args);
          } finally {
            rendering = false;
          }
        };
      });

      // Set all tools to passive by default
      toolGroup.setToolPassive(PanTool.toolName);
      toolGroup.setToolPassive(ZoomTool.toolName);
      toolGroup.setToolPassive(WindowLevelTool.toolName);
      toolGroup.setToolPassive(LengthTool.toolName);
      toolGroup.setToolPassive(AngleTool.toolName);
      toolGroup.setToolPassive(PlanarFreehandROITool.toolName);
      toolGroup.setToolPassive(VelocityProbeTool.toolName);
      toolGroup.setToolPassive(VelocitySegmentTool.toolName);
      toolGroup.setToolPassive(VTITraceTool.toolName);
      toolGroup.setToolPassive(TimeIntervalTool.toolName);
    },
    [renderingEngine, renderingEngineId, reduxStore]
  );

  // Cleanup tool group for a viewport
  const cleanupToolGroupForViewport = useCallback((viewportId: string) => {
    const toolGroupId = `${TOOL_GROUP_PREFIX}${viewportId}`;
    try {
      ToolGroupManager.destroyToolGroup(toolGroupId);
    } catch {
      // Tool group may already be destroyed
    }
  }, []);

  // --- Manipulation tools (W/L, Pan, Zoom): active on ALL viewports ---
  // These tools modify viewport properties and have no persistent annotations.
  // Keeping them active everywhere avoids setToolPassive re-renders on hover,
  // which is critical for VideoViewport W/L (CSS SVG filter persistence).
  // Does NOT depend on activeViewportId — no re-run on hover.
  useEffect(() => {
    if (!renderingEngine || !activeTool || !MANIPULATION_TOOLS.has(activeTool))
      return undefined;

    const tool = activeTool;
    viewportIds.forEach((vpId) => {
      const toolGroup = ToolGroupManager.getToolGroup(
        `${TOOL_GROUP_PREFIX}${vpId}`
      );
      if (toolGroup) {
        try {
          toolGroup.setToolActive(tool, {
            bindings: [{ mouseButton: MouseBindings.Primary }],
          });
        } catch {
          // Tool might not be added yet
        }
      }
    });

    return () => {
      viewportIds.forEach((vpId) => {
        const toolGroup = ToolGroupManager.getToolGroup(
          `${TOOL_GROUP_PREFIX}${vpId}`
        );
        if (toolGroup) {
          try {
            toolGroup.setToolPassive(tool);
          } catch {
            // Ignore
          }
        }
      });
    };
  }, [activeTool, viewportIds, renderingEngine]);

  // --- Annotation tools (Length, Angle, Freehand): active on hovered viewport only ---
  // These create persistent annotations and need single-viewport focus.
  useEffect(() => {
    if (!renderingEngine || !activeTool || MANIPULATION_TOOLS.has(activeTool))
      return undefined;

    // Deactivate on all viewports first
    viewportIds.forEach((vpId) => {
      const toolGroup = ToolGroupManager.getToolGroup(
        `${TOOL_GROUP_PREFIX}${vpId}`
      );
      if (toolGroup) {
        try {
          toolGroup.setToolPassive(cornerstoneTool(activeTool));
        } catch {
          // Tool might not be added yet
        }
      }
    });

    // Activate only on the hovered viewport, and only if its image takes the tool
    if (activeViewportId && canUseViewerTool(activeTool, activeImage)) {
      const toolGroup = ToolGroupManager.getToolGroup(
        `${TOOL_GROUP_PREFIX}${activeViewportId}`
      );
      if (toolGroup) {
        try {
          toolGroup.setToolActive(cornerstoneTool(activeTool), {
            bindings: [{ mouseButton: MouseBindings.Primary }],
          });
        } catch {
          // Tool activation failed - tool may not be registered
        }
      }
    }

    // Passivate this tool on every viewport when activeTool changes — otherwise
    // the previous tool stays bound to the primary mouse button after a switch,
    // so a draw for the new measurement grabs/reshapes the old tool's annotation
    // and the value lands on the wrong measurement.
    const toolToRelease = cornerstoneTool(activeTool);
    return () => {
      viewportIds.forEach((vpId) => {
        const toolGroup = ToolGroupManager.getToolGroup(
          `${TOOL_GROUP_PREFIX}${vpId}`
        );
        if (toolGroup) {
          try {
            toolGroup.setToolPassive(toolToRelease);
          } catch {
            // Tool might not be added yet
          }
        }
      });
    };
  }, [activeViewportId, activeImage, activeTool, viewportIds, renderingEngine]);

  // Change the active tool
  const changeTool = useCallback((tool: string) => {
    // Skip if already the active tool
    if (activeToolRef.current === tool) return;

    activeToolRef.current = tool;
    userActivatedRef.current = true;
    setActiveTool(tool);
  }, []);

  // Deactivate all tools
  const deactivateTool = useCallback(() => {
    if (!activeToolRef.current) return;

    const prevTool = activeToolRef.current;

    // Deactivate on all viewports
    viewportIds.forEach((vpId) => {
      const toolGroupId = `${TOOL_GROUP_PREFIX}${vpId}`;
      const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
      if (toolGroup) {
        try {
          toolGroup.setToolPassive(cornerstoneTool(prevTool));
        } catch {
          // Ignore
        }
      }
    });

    activeToolRef.current = undefined;
    userActivatedRef.current = false;
    setActiveTool(undefined);

    // Reset cursors on viewport DOM elements after Cornerstone finishes processing.
    // setToolPassive() deactivates event bindings but doesn't clear the inline
    // cursor set by setCursorForElement. We use rAF to defer past Cornerstone's
    // own event handlers which may re-apply the cursor in the same event loop tick.
    requestAnimationFrame(() => {
      if (renderingEngine) {
        viewportIds.forEach((vpId) => {
          try {
            const viewport = renderingEngine.getViewport(vpId);
            if (viewport?.element) {
              viewport.element.style.cursor = 'default';
            }
          } catch {
            // Viewport may not exist
          }
        });
      }
      // Blur the focused element so Space key reaches the document-level
      // play/pause hotkey instead of triggering a browser click on the
      // viewport element (which has role="button").
      (document.activeElement as HTMLElement)?.blur?.();
    });
  }, [viewportIds, renderingEngine]);

  // Deactivate tool after a single annotation is completed.
  // This prevents drawing a second annotation before the Redux roundtrip
  // (completeMeasurement → clearMeasurement) has time to propagate.
  // Deferred via rAF so Cornerstone's completion flow (selection, state
  // updates) finishes before the tool is passivated.
  useEffect(() => {
    const handleAnnotationCompleted = (evt: Event) => {
      const { detail } = evt as CustomEvent;
      const ann = detail?.annotation;

      // Tag annotation with measurement label if drawing from worksheet
      const label = measurementLabelRef.current;
      if (label && ann?.data) {
        ann.data.measurementLabel = label;
      }
      const abbr = measurementAbbreviationRef.current;
      if (abbr && ann?.data) {
        ann.data.abbreviation = abbr;
      }

      // Freshly-drawn annotations are always manual (yellow).
      const freshUid = ann?.annotationUID;
      if (freshUid) {
        csAnnotation.config.style.setAnnotationStyles(
          freshUid,
          getBaseAnnotationStyle({ data: { isAI: false } })
        );
        // The measurement just drawn becomes the active one (AP-836).
        dispatch(selectAnnotation(freshUid));
      }

      // Auto-place label outside the geometry for built-in cornerstone tools.
      // Spectral tools place labels themselves in their renderAnnotation.
      autoPlaceBuiltInToolLabel(ann);

      // Deactivation is handled by ViewerPane's measurementStatus effect.
      // For non-measurement tool usage (Alt+L hotkey), deactivate here
      // only if there's no active measurement (no Redux flow to trigger it).
      if (userActivatedRef.current && !measurementLabelRef.current) {
        requestAnimationFrame(() => {
          userActivatedRef.current = false;
          deactivateTool();
        });
      }
    };

    eventTarget.addEventListener(
      ToolEnums.Events.ANNOTATION_COMPLETED,
      handleAnnotationCompleted
    );

    return () => {
      eventTarget.removeEventListener(
        ToolEnums.Events.ANNOTATION_COMPLETED,
        handleAnnotationCompleted
      );
    };

    function autoPlaceBuiltInToolLabel(ann: unknown) {
      if (!ann || typeof ann !== 'object') return;
      const annotation = ann as {
        metadata?: { toolName?: string };
        data?: {
          handles?: {
            points?: number[][];
            textBox?: { worldPosition?: number[] };
          };
        };
      };
      const toolName = annotation.metadata?.toolName;
      if (toolName !== LengthTool.toolName && toolName !== AngleTool.toolName) {
        return;
      }
      const points = annotation.data?.handles?.points;
      if (!points || points.length === 0) return;

      const re = renderingEngineRef.current;
      const vpId = activeViewportIdRef.current;
      if (!re || !vpId) return;
      let viewport: CornerstoneTypes.IViewport | null = null;
      try {
        viewport = re.getViewport(vpId);
      } catch {
        return;
      }
      if (!viewport || !viewport.element) return;

      const canvasPoints = points.map((p) => {
        const c = (
          viewport as unknown as {
            worldToCanvas: (
              w: CornerstoneTypes.Point3
            ) => CornerstoneTypes.Point2;
          }
        ).worldToCanvas([p[0], p[1], p[2] ?? 0] as CornerstoneTypes.Point3);
        return [c[0], c[1]] as CornerstoneTypes.Point2;
      });

      applySmartLabelAnchor({
        annotation: annotation as {
          data: { handles: { textBox?: unknown } };
        },
        geometryCanvasPoints: canvasPoints,
        viewport: viewport as unknown as {
          element: HTMLElement;
          canvasToWorld: (
            p: CornerstoneTypes.Point2
          ) => CornerstoneTypes.Point3;
        },
      });

      // Re-render so the new textbox position is shown immediately.
      try {
        re.renderViewports([vpId]);
      } catch {
        // Render failures here are cosmetic — the next paint will catch up.
      }
    }
  }, [deactivateTool, dispatch]);

  // -- Hover cursor + hover chip toggle --
  // Uses mousemove + rAF so the check runs after Cornerstone updates
  // highlighted flags. Also flips `textBoxVisibility` on the hovered
  // annotation so the abbreviation chip appears; every other annotation
  // stays hidden (textBoxVisibility: false → Cornerstone skips rendering
  // the drag handle entirely, avoiding the empty white rectangles).
  useEffect(() => {
    if (!renderingEngine) return undefined;

    let rafId = 0;
    let hoveredUID: string | null = null;

    const handleMouseMove = (e: MouseEvent) => {
      if (activeToolRef.current) return;

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const all = csAnnotation.state.getAllAnnotations() as {
          highlighted?: boolean;
          annotationUID?: string;
          data?: {
            abbreviation?: string;
            handles?: { textBox?: unknown };
          };
        }[];
        // While the selected annotation is hovered it is the only hovered
        // one, whichever tools the others belong to (AP-806).
        keepHoverOnSelected(all, currentSelectedUID);
        // Locked (finalized) annotations never get the hover flag; hit-test them.
        const flagged = all.find((ann) => ann.highlighted);
        const hitUID = flagged
          ? null
          : annotationUIDAtEvent(e, SELECTABLE_TOOLS);
        const highlighted =
          flagged ?? all.find((ann) => ann.annotationUID === hitUID);
        const newHoveredUID = highlighted?.annotationUID ?? null;
        currentHoveredUID = newHoveredUID;
        const cursor = newHoveredUID ? 'pointer' : 'default';

        viewportIds.forEach((vpId) => {
          try {
            const vp = renderingEngine.getViewport(vpId);
            if (vp?.element) {
              vp.element.style.cursor = cursor;
              if (vp.element.parentElement) {
                vp.element.parentElement.style.cursor = cursor;
              }
            }
          } catch {
            // Viewport may not exist
          }
        });

        // Pin the hover chip to the cursor: place the hovered annotation's
        // textBox at the mouse's world position so the abbreviation appears
        // where the pointer touches the line/contour, not at a fixed anchor.
        let repositioned = false;
        if (
          newHoveredUID &&
          highlighted?.data?.handles &&
          !isDoppler(highlighted)
        ) {
          const getVp = (vpId: string) => {
            try {
              return renderingEngine.getViewport(vpId) as
                | (CornerstoneTypes.IViewport & {
                    element: HTMLElement;
                    canvasToWorld: (p: [number, number]) => number[];
                  })
                | undefined;
            } catch {
              return undefined;
            }
          };
          const containsCursor = (el: HTMLElement) => {
            const rect = el.getBoundingClientRect();
            return (
              e.clientX >= rect.left &&
              e.clientX <= rect.right &&
              e.clientY >= rect.top &&
              e.clientY <= rect.bottom
            );
          };
          const vp = viewportIds
            .map(getVp)
            .find((v) => !!v?.element && containsCursor(v.element));
          if (vp?.element) {
            const rect = vp.element.getBoundingClientRect();
            // Cornerstone's drawTextBox anchors the box ~25px down-right of the
            // worldPosition. Offset the anchor so the chip lands just to the
            // RIGHT of the pointer (roughly level with it), clear of the cursor
            // instead of underneath it.
            const CHIP_ANCHOR_DX = -6; // net ≈ +19px right of the cursor
            const CHIP_ANCHOR_DY = -19; // net ≈ -17px, centered on the cursor
            const world = vp.canvasToWorld([
              e.clientX - rect.left + CHIP_ANCHOR_DX,
              e.clientY - rect.top + CHIP_ANCHOR_DY,
            ]);
            highlighted.data.handles.textBox = {
              hasMoved: true,
              worldPosition: world,
              worldBoundingBox: {
                topLeft: world,
                topRight: world,
                bottomLeft: world,
                bottomRight: world,
              },
            };
            repositioned = true;
          }
        }

        if (newHoveredUID === hoveredUID) {
          // Same annotation, but the chip tracks the cursor → repaint.
          if (repositioned) renderingEngine.renderViewports(viewportIds);
          return;
        }

        // Reset the previously-hovered annotation to its resting style,
        // selected or not.
        if (hoveredUID) {
          const prevAnn = csAnnotation.state.getAnnotation(hoveredUID);
          if (prevAnn) {
            csAnnotation.config.style.setAnnotationStyles(
              hoveredUID,
              getBaseAnnotationStyle(prevAnn, {
                isSelected: hoveredUID === currentSelectedUID,
              })
            );
          }
        }

        // Show the chip on the newly-hovered annotation. Skip a non-Doppler one
        // with no abbreviation to render — otherwise we'd draw an empty rectangle.
        if (newHoveredUID) {
          const nextAnn = csAnnotation.state.getAnnotation(newHoveredUID);
          const hasAbbr = !!(
            nextAnn?.data as { abbreviation?: string } | undefined
          )?.abbreviation;
          if (nextAnn && (hasAbbr || isDoppler(nextAnn))) {
            csAnnotation.config.style.setAnnotationStyles(
              newHoveredUID,
              getBaseAnnotationStyle(nextAnn, {
                isHovered: true,
                isSelected: newHoveredUID === currentSelectedUID,
              })
            );
          }
        }

        hoveredUID = newHoveredUID;
        renderingEngine.renderViewports(viewportIds);
      });
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [renderingEngine, viewportIds]);

  // -- Annotation selection & deletion --
  // Selection state is owned by Redux (`selectedAnnotationUID`). The mousedown
  // handler dispatches; the effect below reacts and updates viewer styling.
  // Worksheet row clicks dispatch through the same channel.
  const selectedUIDRef = useRef<string | null>(null);

  // Watch Redux → drive Cornerstone styling. Runs on click, worksheet click,
  // programmatic clear, and delete-key.
  useEffect(() => {
    if (!renderingEngine) return;
    const newUID = selectedAnnotationUID;
    const prevUID = selectedUIDRef.current;
    if (prevUID === newUID) return;

    if (prevUID) {
      const prevAnn = csAnnotation.state.getAnnotation(prevUID);
      csAnnotation.config.style.setAnnotationStyles(
        prevUID,
        prevAnn ? getBaseAnnotationStyle(prevAnn) : {}
      );
    }
    if (newUID) {
      const nextAnn = csAnnotation.state.getAnnotation(newUID);
      csAnnotation.config.style.setAnnotationStyles(
        newUID,
        nextAnn
          ? getBaseAnnotationStyle(nextAnn, {
              isSelected: true,
              isHovered: !!(nextAnn as { highlighted?: boolean }).highlighted,
            })
          : {}
      );
    }

    selectedUIDRef.current = newUID;
    currentSelectedUID = newUID;
    renderingEngine.renderViewports(viewportIds);
  }, [selectedAnnotationUID, renderingEngine, viewportIds]);

  // Distinguish a click (toggle selection) from a drag (move/resize an
  // annotation) by measuring pointer travel between mousedown and mouseup.
  // Deciding on mousedown alone is wrong: grabbing the selected line's body —
  // or an endpoint before Cornerstone has set `activeHandleIndex` — would
  // toggle selection off underneath the drag, leaving the annotation
  // deselected once the drag ends.
  const pendingDownRef = useRef<{
    uid: string | null;
    x: number;
    y: number;
  } | null>(null);
  const DRAG_THRESHOLD_PX = 4;

  useEffect(() => {
    if (!renderingEngine) return undefined;

    const findClickedUID = (): string | null => {
      const all = csAnnotation.state.getAllAnnotations();
      // A hidden annotation can carry a stale hover flag; never select it.
      const highlighted = all.find(
        (ann: { highlighted?: boolean; annotationUID?: string }) =>
          ann.highlighted &&
          !!ann.annotationUID &&
          csAnnotation.visibility.isAnnotationVisible(ann.annotationUID) !==
            false
      ) as { annotationUID?: string } | undefined;
      return highlighted?.annotationUID ?? null;
    };

    const handleMouseDown = (e: Event) => {
      // Overlays (measurement list, buttons) sit above a stale `highlighted`.
      if (!(e.target as Element | null)?.closest?.('[data-viewport-uid]')) {
        pendingDownRef.current = null;
        return;
      }
      pendingDownRef.current = null;
      // Don't select while drawing. Selection is viewing, so it works without
      // edit rights (finalized report); locking stops the drag itself.
      if (activeToolRef.current) return;
      const me = e as MouseEvent;
      pendingDownRef.current = {
        // Locked annotations (finalized report) never get the hover flag.
        uid: findClickedUID() ?? annotationUIDAtEvent(me, SELECTABLE_TOOLS),
        x: me.clientX,
        y: me.clientY,
      };
    };

    const handleMouseUp = (e: Event) => {
      const pending = pendingDownRef.current;
      pendingDownRef.current = null;
      if (!pending || activeToolRef.current) return;

      const me = e as MouseEvent;
      const moved =
        Math.abs(me.clientX - pending.x) > DRAG_THRESHOLD_PX ||
        Math.abs(me.clientY - pending.y) > DRAG_THRESHOLD_PX;

      // Empty canvas: a click deselects, a drag (pan) keeps the selection.
      if (!pending.uid) {
        if (!moved && selectedUIDRef.current) dispatch(selectAnnotation(null));
        return;
      }

      if (moved) {
        // A drag moved or resized the annotation. Keep it selected — select it
        // if it wasn't already — but never toggle off underneath a drag. Guard
        // on existence so we never select an annotation that's been removed.
        if (
          pending.uid !== selectedUIDRef.current &&
          csAnnotation.state.getAnnotation(pending.uid)
        ) {
          dispatch(selectAnnotation(pending.uid));
        }
        return;
      }

      // Plain click: clicking the selected annotation deselects it; clicking a
      // different one switches selection.
      const nextUID =
        pending.uid === selectedUIDRef.current ? null : pending.uid;
      dispatch(selectAnnotation(nextUID));
    };

    const handleKeyDown = (e: Event) => {
      const { key } = e as KeyboardEvent;
      if (key !== 'Delete' && key !== 'Backspace') return;
      if (!selectedUIDRef.current) return;
      if (!canEdit) return;

      e.preventDefault();
      const uid = selectedUIDRef.current;
      // AP-773 (A3): records the step, then clears style, removes, deselects
      // (the selection effect nulls the refs) and tells the worksheet.
      deleteAnnotationWithHistory(uid, reduxStore);
      renderingEngine.renderViewports(viewportIds);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [renderingEngine, viewportIds, dispatch, reduxStore, canEdit]);

  return {
    activeTool,
    changeTool,
    deactivateTool,
    setupToolGroupForViewport,
    cleanupToolGroupForViewport,
  };
}
